import { formatPriceWithDecimals, formatPriceWithoutDecimals } from '@pkgs/components/utils/numberFormatters';
import { CountryCode, type Currency, type Language, type PricesV2, PropertyMarketingType } from '@ev-tech/search-modules-api';

import { hasPositiveRangeValue } from './utils';
import {
  type ExtraPriceInfo,
  type ListingPriceAdditionalInfo,
  type ListingPriceDescriptor,
  type ListingPriceFieldName,
  type ListingSummary,
} from './types';

const PRICE_ON_REQUEST_KEY = 'search.listingData.price.priceOnRequest' as const;
const DEVELOPMENT_PRICE_LABEL_KEY = 'search.listingData.price.label.development.price' as const;
const GENERIC_RENT_LABEL_KEY = 'search.listingData.price.label.rent' as const;
const PRICE_LABEL_KEYS = {
  salesPrice: 'search.listingData.price.label.salesPrice',
  rentTotal: 'search.listingData.price.label.rentTotal',
  rentNetPerSquareMeter: 'search.listingData.price.label.rentNetPerSquareMeter',
  rentNet: 'search.listingData.price.label.rentNet',
  rentUtilities: 'search.listingData.price.label.monthlyUtilities',
} as const satisfies Partial<Record<ListingPriceFieldName, ListingPriceDescriptor['labelKey']>>;
const PRIMARY_RENT_FIELDS = ['rentTotal', 'rentNetPerSquareMeter', 'rentNet'] as const;
const EXTRA_RENT_FIELDS = ['rentNet', 'rentUtilities'] as const;
const FRENCH_BUYER_TENANT_COMMISSION_TYPES = ['COMMISSIONMIXED', 'COMMISSIONBYERTENANTSIDE'] as const;

function getPriceValue(value: PricesV2[ListingPriceFieldName]): number | undefined {
  if (value?.min && value.min > 0) return value.min;
  if (value?.max && value.max > 0) return value.max;
}

function getPriceValues({ value, returnRangeValue }: { value: PricesV2[ListingPriceFieldName]; returnRangeValue: boolean }): number[] {
  if (returnRangeValue && value?.min && value.min > 0 && value.max && value.max > 0 && value.min !== value.max) {
    return [value.min, value.max];
  }

  const priceValue = getPriceValue(value);
  return priceValue === undefined ? [] : [priceValue];
}

function formatPriceValue({
  fieldName,
  value,
  language,
  currency,
  isConverted = false,
}: {
  fieldName: ListingPriceFieldName;
  value: number;
  language: Language;
  currency: Currency;
  isConverted?: boolean;
}): string {
  const formattedValue =
    fieldName === 'rentNetPerSquareMeter' || fieldName === 'rentUtilities'
      ? formatPriceWithDecimals(value, language, currency)
      : formatPriceWithoutDecimals(value, language, currency);

  return isConverted ? `~${formattedValue}` : formattedValue;
}

function formatPriceRange({
  fieldName,
  value,
  language,
  currency,
  isConverted = false,
  returnRangeValue,
}: {
  fieldName: ListingPriceFieldName;
  value: PricesV2[ListingPriceFieldName];
  language: Language;
  currency: Currency;
  isConverted?: boolean;
  returnRangeValue: boolean;
}): string {
  const formattedValue = getPriceValues({ value, returnRangeValue })
    .map((priceValue) => formatPriceValue({ fieldName, value: priceValue, language, currency }))
    .join(' - ');

  return isConverted ? `~${formattedValue}` : formattedValue;
}

function getPrimaryPriceField(listing: ListingSummary, prices: PricesV2): Pick<ListingPriceDescriptor, 'fieldName' | 'labelKey'> {
  if ('isDevelopmentProject' in listing && listing.isDevelopmentProject) {
    return { fieldName: 'salesPrice', labelKey: DEVELOPMENT_PRICE_LABEL_KEY };
  }
  if (!('propertyMarketingType' in listing) || listing.propertyMarketingType === PropertyMarketingType.sale) {
    return { fieldName: 'salesPrice', labelKey: PRICE_LABEL_KEYS.salesPrice };
  }

  const selectedField = PRIMARY_RENT_FIELDS.find((fieldName) => hasPositiveRangeValue(prices[fieldName]));

  return selectedField
    ? { fieldName: selectedField, labelKey: PRICE_LABEL_KEYS[selectedField] }
    : { fieldName: 'rentTotal', labelKey: GENERIC_RENT_LABEL_KEY };
}

function getConvertedPriceRange({
  listing,
  fieldName,
  baseCurrency,
  currency,
}: {
  listing: ListingSummary;
  fieldName: ListingPriceFieldName;
  baseCurrency: Currency;
  currency: Currency;
}): PricesV2[ListingPriceFieldName] | undefined {
  if (currency === baseCurrency || !listing.price) return;
  const value = listing.price[fieldName];
  return hasPositiveRangeValue(value) ? value : undefined;
}

function getRentalExtraPriceInfo({
  listing,
  baseCurrency,
  currency,
  language,
  primaryFieldName,
  returnRangeValue,
}: {
  listing: ListingSummary;
  baseCurrency: Currency;
  currency: Currency;
  language: Language;
  primaryFieldName: ListingPriceFieldName;
  returnRangeValue: boolean;
}): ListingPriceDescriptor[] {
  if (!('countryAlpha2' in listing) || !('propertyMarketingType' in listing)) return [];

  const isGermanRental =
    listing.countryAlpha2 === CountryCode.DE &&
    listing.propertyMarketingType === PropertyMarketingType.rent &&
    !listing.isDevelopmentProject;
  const isAustrianRental = listing.countryAlpha2 === CountryCode.AT && listing.propertyMarketingType === PropertyMarketingType.rent;

  if (!isGermanRental && !isAustrianRental) return [];

  const prices = listing.basePrice ?? {};
  return EXTRA_RENT_FIELDS.flatMap((fieldName) => {
    const value = prices[fieldName];
    if (!hasPositiveRangeValue(value) || fieldName === primaryFieldName) return [];
    const convertedValue = getConvertedPriceRange({ listing, fieldName, baseCurrency, currency });

    return [
      {
        fieldName,
        labelKey: PRICE_LABEL_KEYS[fieldName],
        value: formatPriceRange({ fieldName, value, language, currency: baseCurrency, returnRangeValue }),
        ...(convertedValue === undefined
          ? {}
          : {
              convertedValue: formatPriceRange({
                fieldName,
                value: convertedValue,
                language,
                currency,
                isConverted: true,
                returnRangeValue,
              }),
            }),
      },
    ];
  });
}

function getCountrySpecificExtraPriceInfo(listing: ListingSummary): ListingPriceAdditionalInfo[] {
  if (!('countryAlpha2' in listing)) return [];

  if (listing.countryAlpha2 === CountryCode.HR && 'propertyTax' in listing && listing.propertyTax?.min) {
    return [
      {
        fieldName: 'propertyTax',
        labelKey: 'search.listingData.price.label.propertyTax',
        value: listing.propertyTax.min,
      },
    ];
  }

  if (listing.countryAlpha2 !== CountryCode.FR || !('commissionType' in listing)) return [];

  if (FRENCH_BUYER_TENANT_COMMISSION_TYPES.some((commissionType) => listing.commissionType === commissionType)) {
    const commissionPercent = 'commissionPercent' in listing ? listing.commissionPercent : undefined;
    if (commissionPercent?.min) {
      return [
        {
          fieldName: 'commissionPercent',
          labelKey: 'search.listingData.price.label.commissionPercent',
          value: commissionPercent.min,
        },
      ];
    }

    return commissionPercent ? [{ fieldName: 'sellerCommission', labelKey: 'search.listingData.price.label.sellerCommission' }] : [];
  }

  return listing.commissionType === 'COMMISSIONOWNERSIDE'
    ? [{ fieldName: 'sellerCommission', labelKey: 'search.listingData.price.label.sellerCommission' }]
    : [];
}

function getExtraPriceInfo({
  listing,
  baseCurrency,
  currency,
  language,
  primaryFieldName,
  returnRangeValue,
}: {
  listing: ListingSummary;
  baseCurrency: Currency;
  currency: Currency;
  language: Language;
  primaryFieldName: ListingPriceFieldName;
  returnRangeValue: boolean;
}): ExtraPriceInfo[] {
  return [
    ...getRentalExtraPriceInfo({ listing, baseCurrency, currency, language, primaryFieldName, returnRangeValue }),
    ...getCountrySpecificExtraPriceInfo(listing),
  ];
}

/**
 * Selects and formats the primary listing price for direct rendering.
 *
 * `currency` is the requested display currency and `language` controls number formatting.
 * The returned `value` is the formatted base-currency price. If a matching price in the
 * requested currency is available, `convertedValue` contains the formatted value prefixed
 * with `~`. Price-on-request listings omit both values and provide
 * `valueKey: 'search.listingData.price.priceOnRequest'` instead.
 *
 * Set `returnExtraPriceInfo` to `true` to add applicable German and Austrian rental,
 * Croatian property-tax, and French commission details in `extraPriceInfo`; it defaults
 * to `false`.
 *
 * Set `returnRangeValue` to `true` to format distinct positive minimum and maximum
 * values as a range. It defaults to `false`, returning the minimum value when available,
 * otherwise the maximum value.
 */
export function getListingPrice(
  listing: ListingSummary,
  {
    currency,
    language,
    returnExtraPriceInfo = false,
    returnRangeValue = false,
  }: {
    currency: Currency;
    language: Language;
    returnExtraPriceInfo?: boolean;
    returnRangeValue?: boolean;
  },
): ListingPriceDescriptor | (ListingPriceDescriptor & { extraPriceInfo: ExtraPriceInfo[] }) {
  const baseCurrency = listing.baseCurrency ?? currency;
  const prices = listing.basePrice ?? {};
  const { fieldName, labelKey } = getPrimaryPriceField(listing, prices);
  const hasPriceOnRequest = listing.hasPriceOnRequest || !hasPositiveRangeValue(prices[fieldName]);
  const convertedValue = getConvertedPriceRange({ listing, fieldName, baseCurrency, currency });

  const descriptor: ListingPriceDescriptor = hasPriceOnRequest
    ? { fieldName, labelKey, valueKey: PRICE_ON_REQUEST_KEY }
    : {
        fieldName,
        labelKey,
        value: formatPriceRange({ fieldName, value: prices[fieldName], language, currency: baseCurrency, returnRangeValue }),
        ...(convertedValue === undefined
          ? {}
          : {
              convertedValue: formatPriceRange({
                fieldName,
                value: convertedValue,
                language,
                currency,
                isConverted: true,
                returnRangeValue,
              }),
            }),
      };

  if (!returnExtraPriceInfo) return descriptor;

  return {
    ...descriptor,
    extraPriceInfo: getExtraPriceInfo({
      listing,
      baseCurrency,
      currency,
      language,
      primaryFieldName: fieldName,
      returnRangeValue,
    }),
  };
}
